import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { getProduct, products } from '../data/products';
import { getProgram } from '../data/programs';
import { Button } from '../components/ui/Button';
import { Eyebrow } from '../components/ui/Eyebrow';
import { ProductCard } from '../components/shop/ProductCard';
import { CtaBand } from '../components/ui/CtaBand';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProduct(slug) : undefined;
  const {
    brand,
    text,
    productImage
  } = useBrand();
  useDocumentMeta({
    title: product ? `${product.name} — ${brand.name} Pharmacy` : `Pharmacy — ${brand.name}`,
    description: product ? `${product.tagline} ${product.price} ${product.cadence} — ${product.priceNote}.` : 'FDA-approved medications and US-compounded peptides, priced in public.',
    image: product ? productImage(product.slug, product.image) : brand.images.hero
  });
  if (!product) {
    return <main className="bg-cream" data-id="element-461" data-mp="3aw47poh69qo8">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-462" data-mp="3awxaked0fw13">
          <Eyebrow className="text-brick" data-id="element-463" data-mp="3fup7xnwfcaco">Not found</Eyebrow>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl leading-tight text-ink md:text-6xl" data-id="element-464" data-mp="3fv764q2ncyn8">
            We could not find that medication.
          </h1>
          <Link to="/shop" className="mt-8 inline-flex items-center gap-2 text-sm text-brick hover:text-ink" data-id="element-465" data-mp="bh0hjxtc1ddi">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-466" data-mp="3tog7q4kcsfv7" />
            Back to the pharmacy
          </Link>
        </div>
      </main>;
  }
  const program = getProgram(product.program);
  const related = products.filter(item => item.slug !== product.slug).slice(0, 3);
  return <main data-id="element-467" data-mp="2n7us54ajxz5j">
      <section className="bg-cream" data-id="element-468" data-mp="2n7l07b5y5hx7">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-12 md:px-8 md:pb-24 md:pt-16" data-id="element-469" data-mp="233xa43h14mai">
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-150 ease-republic hover:text-ink" data-id="element-470" data-mp="1z2k1xmqgs70f">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-471" data-mp="1u9ilhaalzzeq" />
            Pharmacy
          </Link>

          <div className="mt-10 grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16" data-id="element-472" data-mp="1pao48csaiiwq">
            <img src={productImage(product.slug, product.image)} alt={`${product.name}, ${product.form}`} className="aspect-square w-full rounded-brand bg-stone-light object-cover" data-id="element-473" data-mp="1kf8offaq2cxo" />

            <div data-id="element-474" data-mp="16dweplm5n7k8">
              <Eyebrow className={product.category === 'FDA-approved' ? 'text-brick' : 'text-ink-muted'} data-id="element-475" data-mp="16efxt5njk1zu">
                {product.status}
              </Eyebrow>
              <h1 className="mt-4 font-serif text-[2.75rem] leading-[1.02] text-ink md:text-[4rem]" data-id="element-476" data-mp="35oo1ffzr3uga">
                {product.name}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-477" data-mp="38af0beeld5v8">
                {text(product.description)}
              </p>

              <div className="rule mt-10 pt-8" data-id="element-478" data-mp="3d86pi0nuqdvq">
                <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-479" data-mp="3i2axmn8usrye">
                  <div data-id="element-480" data-mp="3n0ulit70cdte">
                    <p className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-481" data-mp="3mzb6t7102atq">
                      Published price
                    </p>
                    <p className="mt-3 font-serif text-5xl leading-none text-ink" data-id="element-482" data-mp="2ubnoxzr45yas">
                      ${product.price}
                      <span className="ml-3 font-sans text-sm text-ink-muted" data-id="element-483" data-mp="2za8mfreze3vj">
                        {product.cadence}
                      </span>
                    </p>
                    <p className="mt-3 text-sm text-ink-muted" data-id="element-484" data-mp="ekjcnst4x89p">{product.priceNote}</p>
                  </div>
                  <div className="text-sm text-ink-muted" data-id="element-485" data-mp="ilurr4edub3n">
                    <p data-id="element-486" data-mp="ikbcnh4gq63g">+ $39/mo {brand.name} membership</p>
                    <p className="mt-1 text-ink" data-id="element-487" data-mp="h45x4fcga2a4">Rate held for 12 months</p>
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-4" data-id="element-488" data-mp="c75z8rpr85u3">
                  <Button to="/get-started" data-id="element-489" data-mp="r2vca0cyp8mo">
                    Request this medication
                    <ArrowRightIcon className="h-4 w-4" data-id="element-490" data-mp="m423umcj87td" />
                  </Button>
                  {program && <Button to={`/programs/${program.slug}`} variant="secondary" data-id="element-491" data-mp="103bjnkwtszwt">
                      See the {program.name} program
                    </Button>}
                </div>
                <p className="mt-5 max-w-lg text-xs leading-relaxed text-ink-muted" data-id="element-492" data-mp="19twbmphzv66r">
                  Prescription required. Requesting a medication starts a clinical review — you are
                  not charged until a clinician approves a protocol and you accept it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-493" data-mp="1xh06ykmu6guw">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-494" data-mp="1xibcaovxjwo4">
          <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20" data-id="element-495" data-mp="22gmlcxeei35a">
            <div data-id="element-496" data-mp="1ogbtps59okj2">
              <h2 className="font-serif text-3xl leading-tight text-ink md:text-4xl" data-id="element-497" data-mp="1ogv1wh547ujw">
                What you are getting
              </h2>
              <ul className="mt-8 flex flex-col gap-4" data-id="element-498" data-mp="1tdvaaypft9e6">
                {product.highlights.map(highlight => <li key={highlight} className="flex items-start gap-3" data-id="element-499" data-mp="1eofjxjttj5z0">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2} data-id="element-500" data-mp="1jitjlyfijz9h" />
                    <span className="text-base leading-relaxed text-ink-soft" data-id="element-501" data-mp="15r5am40k9qwq">{highlight}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-502" data-mp="vrebfp122nqb">
              <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-503" data-mp="vsz7xk6xkgqt">
                Dispensing detail
              </h2>
              <dl className="mt-6" data-id="element-504" data-mp="10slitmzadkwg">
                <div className="rule flex items-baseline justify-between gap-6 py-4" data-id="element-505" data-mp="32g6k8q3837zw">
                  <dt className="text-sm text-ink-muted" data-id="element-506" data-mp="2xoojo35439fg">Pharmacy</dt>
                  <dd className="text-right text-sm text-ink" data-id="element-507" data-mp="2xndfw3bo21oc">{product.pharmacy}</dd>
                </div>
                {product.facts.map(fact => <div key={fact.label} className="rule flex items-baseline justify-between gap-6 py-4" data-id="element-508" data-mp="2opkzevu7knzd">
                    <dt className="text-sm text-ink-muted" data-id="element-509" data-mp="3ldhl2f4i69hg">{fact.label}</dt>
                    <dd className="max-w-[60%] text-right text-sm text-ink" data-id="element-510" data-mp="3hcxmr8870ztq">{fact.value}</dd>
                  </div>)}
                <div className="rule flex items-baseline justify-between gap-6 py-4" data-id="element-511" data-mp="3ccjdxnyrplun">
                  <dt className="text-sm text-ink-muted" data-id="element-512" data-mp="37e84vfgarfdh">Prescription</dt>
                  <dd className="text-right text-sm text-ink" data-id="element-513" data-mp="20zgzayg8b6d4">
                    {product.rxRequired ? 'Required' : 'Not required'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-514" data-mp="1lttcg88fku3p">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-515" data-mp="1quhc1iaq6j9j">
          <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-516" data-mp="1vu3npnawnrg8">
            Also in the pharmacy
          </h2>
          <ul className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-517" data-mp="20l25krbjkxll">
            {related.map(item => <ProductCard key={item.slug} product={item} data-id="element-518" data-mp="24ldsybvahc0r" />)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-519" data-mp="29jxdc7u8ifr0" />
    </main>;
}